<script setup>
// "Analytics" tab: allocation by type, sector → industry, security, currency (docs/analytics/SPEC.md).
// Every share opens in place to its securities (decision 10).
import { computed, reactive, ref, watch } from 'vue';
import { t } from '../i18n';
import { buildAllocation } from '../analytics';
import { portfolioTotals } from '../portfolioView';
import { formatNumber } from '../format';
import { isPhone } from '../isPhone';
import { readUiState, writeUiState } from '../uiState';
import { CHIP_ROW, chipClass } from '../ui';
import AnalyticsSummary from './AnalyticsSummary.vue';
import DonutChart from './DonutChart.vue';
import PerformanceChart from './PerformanceChart.vue';

const props = defineProps({
  portfolio: {
    type: Object,
    required: true,
  },
  // uid → { price, time } (docs/live-prices/SPEC.md)
  livePrices: {
    type: Object,
    default: () => ({}),
  },
  fxRates: {
    type: Object,
    default: () => ({}),
  },
  // Calendar items for the next 12 months of this view: passive income in the summary
  forwardPayouts: {
    type: Array,
    default: () => [],
  },
});

const allocation = computed(() => buildAllocation(props.portfolio.positions, props.livePrices, props.fxRates));

// Today's point of the growth chart uses the same live total as the summary (docs/performance/SPEC.md).
const liveToday = computed(() => portfolioTotals(props.portfolio, props.livePrices, props.fxRates).total);

const cashValue = computed(() => allocation.value.types.find((s) => s.key === 'currency')?.value ?? 0);

// Opened rows per block; everything starts collapsed and resets when the portfolio changes.
const expanded = reactive({ types: [], sectors: [], currencies: [] });
watch(() => props.portfolio.key, () => {
  expanded.types = [];
  expanded.sectors = [];
  expanded.currencies = [];
});

const BLOCKS = computed(() => ({
  types: allocation.value.types,
  sectors: allocation.value.sectors,
  currencies: allocation.value.currencies,
}));

function expandable(block) {
  return BLOCKS.value[block].filter((s) => (s.items?.length ?? 0) > 0).map((s) => s.key);
}

function allExpanded(block) {
  const keys = expandable(block);
  return keys.length > 0 && keys.every((k) => expanded[block].includes(k));
}

function toggleAll(block) {
  expanded[block] = allExpanded(block) ? [] : expandable(block);
}

// One opened sector: the ring shows its industries (decision 5); otherwise all sectors.
const openedSector = computed(() => (expanded.sectors.length === 1
  ? allocation.value.sectors.find((s) => s.key === expanded.sectors[0]) ?? null
  : null));

const sectorRing = computed(() => (openedSector.value && (openedSector.value.groups?.length ?? 0) > 1
  ? openedSector.value.groups
  : null));

function percent(value) {
  return `${formatNumber(value, 1)} %`;
}

// Phone: one «Структура» card with chips instead of four ring cards (docs/design/SPEC.md, «Аналитика»).
// New allocations become one more chip, not one more card (decision 12).
const STRUCTURE = ['types', 'sectors', 'companies', 'currencies'];
const PHONE_LIMIT = 5;
const savedStructure = readUiState('analytics.structure', 'types');
const structure = ref(STRUCTURE.includes(savedStructure) ? savedStructure : 'types');
watch(structure, (value) => writeUiState('analytics.structure', value));
</script>

<template>
  <div class="space-y-3 sm:space-y-4">
    <AnalyticsSummary
      :portfolio="portfolio"
      :live-prices="livePrices"
      :fx-rates="fxRates"
      :forward-payouts="forwardPayouts"
      :cash-value="cashValue"
    />

    <PerformanceChart
      :portfolio="portfolio"
      :live-today="liveToday"
    />

    <p
      v-if="allocation.total <= 0"
      class="rounded-3xl bg-slate-900 p-6 text-center text-[0.9375rem] text-slate-400 sm:rounded-lg sm:border sm:border-dashed sm:border-slate-700 sm:bg-transparent sm:p-8 sm:text-sm"
    >
      {{ t('analytics.empty') }}
    </p>

    <section
      v-else-if="isPhone"
      class="rounded-3xl bg-slate-900 p-5"
    >
      <div class="flex items-center justify-between gap-3">
        <h3 class="text-[1.0625rem] font-semibold text-slate-100">
          {{ t('structure.title') }}
        </h3>
        <button
          v-if="structure !== 'companies' && expandable(structure).length > 0"
          type="button"
          class="-mr-2 flex h-11 items-center rounded-lg px-2 text-[0.8125rem] text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          @click="toggleAll(structure)"
        >
          {{ allExpanded(structure) ? t('analytics.collapseAll') : t('analytics.expandAll') }}
        </button>
      </div>
      <div
        :class="[CHIP_ROW, 'mt-3']"
        role="tablist"
        :aria-label="t('structure.title')"
      >
        <button
          v-for="key in STRUCTURE"
          :key="key"
          type="button"
          role="tab"
          :aria-selected="structure === key"
          :class="chipClass(structure === key)"
          @click="structure = key"
        >
          {{ t(`structure.${key}`) }}
        </button>
      </div>
      <p
        v-if="allocation.skipped > 0"
        class="mt-3 text-[0.8125rem] text-slate-500"
      >
        {{ t('analytics.skipped', { count: allocation.skipped }) }}
      </p>

      <div class="mt-5">
        <DonutChart
          v-if="structure === 'types'"
          :key="structure"
          v-model:expanded="expanded.types"
          :slices="allocation.types"
          :total="allocation.total"
          :portfolio-total="allocation.total"
          :center-label="t('analytics.total')"
          :limit="PHONE_LIMIT"
        />
        <DonutChart
          v-else-if="structure === 'sectors'"
          :key="structure"
          v-model:expanded="expanded.sectors"
          :slices="allocation.sectors"
          :ring="sectorRing"
          :total="sectorRing ? openedSector.value : allocation.total"
          :portfolio-total="allocation.total"
          :center-label="sectorRing ? openedSector.label : t('analytics.total')"
          :limit="PHONE_LIMIT"
        />
        <!-- Without the Top-5 / Top-10 line on a phone (docs/design/SPEC.md, decision 20) -->
        <DonutChart
          v-else-if="structure === 'companies'"
          :key="structure"
          :slices="allocation.companies.slices"
          :others="allocation.companies.others"
          :total="allocation.companies.total"
          :portfolio-total="allocation.total"
          :center-label="t('analytics.securities')"
          numbered
        />
        <!-- A ring even with a single currency (decision 18) -->
        <DonutChart
          v-else
          :key="structure"
          v-model:expanded="expanded.currencies"
          :slices="allocation.currencies"
          :total="allocation.total"
          :portfolio-total="allocation.total"
          :center-label="t('analytics.total')"
          :limit="PHONE_LIMIT"
        />
      </div>
    </section>

    <template v-else>
      <p
        v-if="allocation.skipped > 0"
        class="text-xs text-slate-500"
      >
        {{ t('analytics.skipped', { count: allocation.skipped }) }}
      </p>

      <div class="space-y-4">
        <section
          v-for="block in ['types', 'sectors']"
          :key="block"
          class="min-w-0 rounded-lg border border-slate-700 bg-slate-900/40 p-4 sm:p-5"
        >
          <div class="mb-4 flex items-center justify-between gap-2">
            <h3 class="truncate text-[0.8125rem] font-semibold text-slate-200">
              {{ t(`analytics.${block}`) }}
            </h3>
            <button
              type="button"
              class="shrink-0 rounded px-2 py-0.5 text-[0.6875rem] text-slate-400 hover:bg-slate-800 hover:text-slate-200"
              @click="toggleAll(block)"
            >
              {{ allExpanded(block) ? t('analytics.collapseAll') : t('analytics.expandAll') }}
            </button>
          </div>
          <DonutChart
            v-if="block === 'types'"
            v-model:expanded="expanded.types"
            :slices="allocation.types"
            :total="allocation.total"
            :portfolio-total="allocation.total"
            :center-label="t('analytics.total')"
          />
          <DonutChart
            v-else
            v-model:expanded="expanded.sectors"
            :slices="allocation.sectors"
            :ring="sectorRing"
            :total="sectorRing ? openedSector.value : allocation.total"
            :portfolio-total="allocation.total"
            :center-label="sectorRing ? openedSector.label : t('analytics.total')"
          />
        </section>

        <section class="min-w-0 rounded-lg border border-slate-700 bg-slate-900/40 p-4 sm:p-5">
          <div class="mb-4 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <h3 class="text-[0.8125rem] font-semibold text-slate-200">
              {{ t('analytics.companies') }}
              <span class="ml-1 text-[0.6875rem] font-normal text-slate-500">{{ t('analytics.companiesCount', { count: allocation.companies.count }) }}</span>
            </h3>
            <p
              v-if="allocation.companies.concentration"
              class="text-[0.8125rem] tabular-nums text-slate-100"
            >
              {{ t('analytics.top5', { percent: percent(allocation.companies.concentration.top5) }) }}
              <template v-if="allocation.companies.concentration.top10 !== null">
                · {{ t('analytics.top10', { percent: percent(allocation.companies.concentration.top10) }) }}
              </template>
            </p>
          </div>
          <DonutChart
            :slices="allocation.companies.slices"
            :others="allocation.companies.others"
            :total="allocation.companies.total"
            :portfolio-total="allocation.total"
            :center-label="t('analytics.securities')"
            numbered
          />
        </section>

        <section class="min-w-0 rounded-lg border border-slate-700 bg-slate-900/40 p-4 sm:p-5">
          <div class="mb-4 flex items-center justify-between gap-2">
            <h3 class="truncate text-[0.8125rem] font-semibold text-slate-200">
              {{ t('analytics.currencies') }}
            </h3>
            <button
              type="button"
              class="shrink-0 rounded px-2 py-0.5 text-[0.6875rem] text-slate-400 hover:bg-slate-800 hover:text-slate-200"
              @click="toggleAll('currencies')"
            >
              {{ allExpanded('currencies') ? t('analytics.collapseAll') : t('analytics.expandAll') }}
            </button>
          </div>
          <DonutChart
            v-model:expanded="expanded.currencies"
            :slices="allocation.currencies"
            :total="allocation.total"
            :portfolio-total="allocation.total"
            :center-label="t('analytics.total')"
          />
        </section>
      </div>
    </template>
  </div>
</template>
